import React, { useEffect, useState } from 'react';
import { match, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';

import { useTheme } from '../../hooks/common/useTheme';

import NavBar, { View } from '../../components/NavBar';
import ProviderDirectory from './Provider';

import * as api from '../../api';

import * as store from '../../reducers/store';
import * as rootReducer from '../../reducers/root';
import {
  CenteredLoader,
  KRIEGER_ORGANIZATION_IDENTIFIER,
  MIT_ORGANIZATION_IDENTIFIER,
  NKCH_ORGANIZATION_IDENTIFIER,
} from './Provider/Common';

interface StateProps {
  organizationId: string;
  organizationSlug: string | null;
  embedded: boolean | null;
}
interface DispatchProps {
  setOrganizationId: (organizationId: string) => void;
  setOrganizationSlug: (setOrganizationSlug: string | null) => void;
  setEmbedded: (embedded: boolean) => void;
}
interface Props extends DispatchProps, StateProps {
  match: match<{ organizationSlug: string }>;
}

const TESTING_ORGANIZATION_IDENTIFIERS = [
  KRIEGER_ORGANIZATION_IDENTIFIER,
  MIT_ORGANIZATION_IDENTIFIER,
  NKCH_ORGANIZATION_IDENTIFIER,
];

const Directory: React.FC<Props> = ({
  match,
  setOrganizationId,
  setOrganizationSlug,
  setEmbedded,
}) => {
  const embedded = window.location !== window.parent.location;
  const { organizationSlug } = match.params;
  const [stateOrgId, setStateOrgId] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    setEmbedded(embedded);
    if (!organizationSlug) {
      setOrganizationSlug(organizationSlug);
    }
    async function getOrganizationId() {
      try {
        const organizationId =
          await api.client.PrevisitOrganization.getOrganizationId(
            organizationSlug,
          );
        setStateOrgId(organizationId);
        setOrganizationId(organizationId);
      } catch (err) {
        setError('Unable to load provider directory');
      }
    }
    if (!stateOrgId) {
      getOrganizationId();
    }
  }, [
    organizationSlug,
    stateOrgId,
    embedded,
    setOrganizationId,
    setOrganizationSlug,
    setEmbedded,
  ]);

  if (error) {
    throw new Error();
  }
  const { isThemeLoading } = useTheme();
  if (!stateOrgId || isThemeLoading) {
    return <CenteredLoader />;
  }

  if (!TESTING_ORGANIZATION_IDENTIFIERS.includes(stateOrgId)) {
    throw new Error();
  }
  return (
    <div id="embedded-app" className="d-flex flex-column h-100">
      {!embedded && <NavBar view={View.Directory} />}
      <Switch>
        <Route
          path={`${match.path}/providers`}
          render={(props) => <ProviderDirectory {...props} />}
        />
      </Switch>
    </div>
  );
};

export function mapStateToProps({
  inbound: { organizationId, organizationSlug },
  embedded,
}: store.All): StateProps {
  return {
    organizationId,
    organizationSlug,
    embedded,
  };
}

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setOrganizationId: (organizationId: string) =>
      dispatch(rootReducer.setOrganizationId(organizationId)),
    setOrganizationSlug: (organizationSlug: string | null) =>
      dispatch(rootReducer.setOrganizationSlug(organizationSlug)),
    setEmbedded: (embedded: boolean | null) =>
      dispatch(rootReducer.setEmbedded(embedded)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Directory);
